//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import PluginInfoModal from '@/components/PluginInfoModal';
import PluginIframeModal from '@/components/PluginIframeModal';
import { assertNever } from '@/utils/AssertNever';
import {
  type ActivePlugin, type PluginSurface
} from '@/services/apiTypes';

interface PluginSurfaceDispatcherProps {
  plugin: ActivePlugin;
  surface: PluginSurface;
  onClose: () => void;
}

/**
 * Picks the modal renderer for a plugin surface based on `surface.kind`.
 * The exhaustive switch + `assertNever` default is a compile-time guard:
 * if `PluginSurface` grows a new variant without a renderer arm here,
 * TypeScript fails the build.  At runtime the default is unreachable
 * because `collectPluginSurfaceRows` already rejected unknown kinds, but
 * if it ever fires the message points at the contract bug.
 */
function PluginSurfaceDispatcher(
  { plugin, surface, onClose }: PluginSurfaceDispatcherProps
) {
  switch (surface.kind) {
    case 'json':
      return (
        <PluginInfoModal
          plugin={plugin}
          surface={surface}
          onClose={onClose}
        />
      );
    case 'webview':
      return (
        <PluginIframeModal
          plugin={plugin}
          surface={surface}
          onClose={onClose}
        />
      );
    default:
      return assertNever(surface);
  }
}

export default PluginSurfaceDispatcher;
